import React, {Component} from "react"
import Papa from "papaparse"

import {Label} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import makeCSV from "@irati/utils/csv/makeCSV"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {OrganisationalSummaryCategory, PaymentCurrencyType, OrganisationalSummary, OrganisationalSummaryTrendDate} from "@irati/types"
import theme from "@irati/ui/src/global/theme"
import ShareLink from "../../../ShareLink"

interface Props extends InjectIntl {
  res: OrganisationalSummary
  category: OrganisationalSummaryCategory
  currencyCode: PaymentCurrencyType
  isTeamSelected: boolean
  onCallback: () => void
}

class OrganisationalSummaryDownload extends Component<Props> {
  getValue = (value?: number) => {
    return value || 0
  }

  getHeaders = () => {
    const {category, formatMessage, isTeamSelected} = this.props

    const initialHeaders = isTeamSelected
      ? [formatMessage({id: "admin.reporting.orgSummary.date.title"})]
      : [
          formatMessage({id: "admin.reporting.orgSummary.date.title"}),
          formatMessage({id: "admin.reporting.orgSummary.main.engagementRate.title"})
        ]

    if (category === OrganisationalSummaryCategory.Perks) {
      return [
        ...initialHeaders,
        formatMessage({id: "admin.reporting.orgSummary.perks.numUsersRedeemed.title"}),
        formatMessage({id: "admin.reporting.orgSummary.perks.numRedemptions.title"}),
        formatMessage({id: "admin.reporting.orgSummary.perks.flexiPointsBurned.title"})
      ]
    }
    if (category === OrganisationalSummaryCategory.Celebrations) {
      return [
        ...initialHeaders,
        formatMessage({id: "admin.reporting.orgSummary.celebrations.numUsersEngaged.title"}),
        formatMessage({id: "admin.reporting.orgSummary.celebrations.interactions.title"}),
        formatMessage({id: "admin.reporting.orgSummary.celebrations.rewardPointsGiven.title"}),
        formatMessage({id: "admin.reporting.orgSummary.celebrations.rewardPointsPurchased.title"})
      ]
    }
    if (category === OrganisationalSummaryCategory.Wellness) {
      return [
        ...initialHeaders,
        formatMessage({id: "admin.reporting.orgSummary.wellness.numUsersEngaged.title"}),
        formatMessage({id: "admin.reporting.orgSummary.wellness.numViews.title"}),
        formatMessage({id: "admin.reporting.orgSummary.wellness.numWebinarRegistrations.title"})
      ]
    }

    return [
      ...initialHeaders,
      formatMessage({id: "admin.reporting.orgSummary.main.numEngagements.title"}),
      formatMessage({id: "admin.reporting.orgSummary.main.totalSavings.title"}),
      formatMessage({id: "admin.reporting.orgSummary.main.discountVoucherSavings.title"}),
      formatMessage({id: "admin.reporting.orgSummary.main.rewardPointSavings.title"}),
      formatMessage({id: "admin.reporting.orgSummary.main.flexiPointSavings.title"}),
      formatMessage({id: "admin.reporting.orgSummary.main.giftCardSavings.title"})
    ]
  }

  getInitialData = (date: OrganisationalSummaryTrendDate, engagementRateValue: number, numberOfActivatedUsers: number) => {
    const {isTeamSelected} = this.props
    const engagementRate = (engagementRateValue / numberOfActivatedUsers) * 100
    const cappedEngagementRate = engagementRate > 100 ? 100 : Math.round(engagementRate)

    if (isTeamSelected) {
      return [`${date.month.value} / ${date.year.value}`]
    }
    return [`${date.month.value} / ${date.year.value}`, `${cappedEngagementRate}%`]
  }

  getData = () => {
    const {res, category, currencyCode} = this.props

    if (category === OrganisationalSummaryCategory.Perks) {
      return res.perks.trends.map((trend) => {
        return [
          ...this.getInitialData(trend.date, trend.usersRedeemed?.value, trend.numberOfActivatedUsers?.value),
          this.getValue(trend.usersRedeemed?.value),
          this.getValue(trend.redemptions?.value),
          this.getValue(trend.flexiPointsBurned?.value)
        ]
      })
    }
    if (category === OrganisationalSummaryCategory.Celebrations) {
      return res.celebrations.trends.map((trend) => {
        return [
          ...this.getInitialData(trend.date, trend.usersEngaged?.value, trend.numberOfActivatedUsers?.value),
          this.getValue(trend.usersEngaged?.value),
          this.getValue((trend.recognitions?.value || 0) + (trend.polls?.value || 0)),
          this.getValue(trend.rewardPointsGiven?.value),
          this.getValue(trend.rewardPointsPurchased?.value)
        ]
      })
    }
    if (category === OrganisationalSummaryCategory.Wellness) {
      return res.wellness.trends.map((trend) => {
        return [
          ...this.getInitialData(trend.date, trend.usersEngaged?.value, trend.numberOfActivatedUsers?.value),
          this.getValue(trend.usersEngaged?.value),
          this.getValue(trend.views?.value),
          this.getValue(trend.webinarRegistrations?.value)
        ]
      })
    }

    return res.main.trends.map((trend) => {
      const estimedSavings =
        currencyCode === PaymentCurrencyType.Gbp
          ? trend.savings?.estimated?.discountVouchersGbp
          : trend.savings?.estimated?.discountVouchersAud
      const rewardSavings = currencyCode === PaymentCurrencyType.Gbp ? trend.savings?.rewardPointsGbp : trend.savings?.rewardPointsAud
      const flexiSavings = currencyCode === PaymentCurrencyType.Gbp ? trend.savings?.flexiPointsGbp : trend.savings?.flexiPointsAud
      const giftCardSavings =
        currencyCode === PaymentCurrencyType.Gbp ? trend.savings?.digitalVouchersGbp : trend.savings?.digitalVouchersAud
      const savings = estimedSavings! + rewardSavings! + flexiSavings! + giftCardSavings! || 0

      return [
        ...this.getInitialData(trend.date, trend.numberOfEngagedUsers?.value, trend.numberOfActivatedUsers?.value),
        this.getValue(trend.numberOfEngagements?.value),
        formatCurrency({value: savings, currencyCode, isCents: false}),
        formatCurrency({value: estimedSavings, currencyCode, isCents: false}),
        formatCurrency({value: rewardSavings, currencyCode, isCents: false}),
        formatCurrency({value: flexiSavings, currencyCode, isCents: false}),
        formatCurrency({value: giftCardSavings, currencyCode, isCents: false})
      ]
    })
  }

  downloadReport = () => {
    const headers = this.getHeaders()
    const data = this.getData()
    const csv = Papa.unparse({fields: headers, data})
    makeCSV(csv, "trendRates.csv")
    this.props.onCallback()
  }

  render() {
    return (
      <Flex justifyContent="space-between" alignItems="center" marginTop={1}>
        <Flex direction="column">
          <Label>
            <FormattedMessage id="admin.reporting.popularPerks.dataInfo" />
          </Label>
        </Flex>
        <Flex gap={theme.spacing.oneAndHalf} minWidth="320px">
          <ShareLink />
          <Button small primary onClick={this.downloadReport} id="adminSection_reporting_downloadTrendRates_button">
            <FormattedMessage id="admin.reporting.button.downloadReport" />
          </Button>
        </Flex>
      </Flex>
    )
  }
}

export default injectIntl(OrganisationalSummaryDownload)
